import React, { useEffect } from 'react'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import { string, bool, shape, arrayOf, func, number } from 'prop-types'

import { actions as personalisationActions } from 'services/app-state/personalizedRecommendation'
import { getCookieByname } from 'services/utility'
import ProductList from 'shared/interim/product-list'
import { trackSignalEvent } from 'services/utility/analytics'
import { getCurrentOS } from 'shared/util/env'
import { P13N_MODULE_CMPID } from 'services/utility/constants'
import { parsePersonalisationData } from 'services/app-state/personalizedRecommendation/actions/personalisationParser'
import { favouritesListParser } from 'services/app-state/personalizedRecommendation/actions/favouritesListParser'
import { serviceEligibility } from 'services/app-state/profile/actions'
import Button from 'src/common/components/button'

import Title from './title'

import './personalizedRecommendation-offers.scss'

const moduleType = 'ProductListingP13N'

const P13N = (props) => {
  const {
    isSignedIn,
    profileId,
    isActive,
    lastLoginAt,
    postCode,
    storeId,
    moduleData,
    personalizedRecommendation: { items = [], metadata = {} },
    serviceEligibilityAction,
    personalisationActionsAPI,
    name,
    type,
    moduleId,
    placementId,
  } = props

  const showRecommendation = getCookieByname('showP13N')
  if (!isSignedIn || !showRecommendation) return null

  const getPersonalisationData = (params) => {
    const {
      laodRecommendations, fetchFavouritesItemList,
      recommendationsAPISuccess, recommendationsAPIFail,
    } = personalisationActionsAPI
    Promise.all([laodRecommendations(params),
      fetchFavouritesItemList()]).then((response) => {
      const [personalisationResponse, favouritesItemResponse] = response
      const favouritesItemIds = favouritesListParser(favouritesItemResponse)
      const personalisationData = parsePersonalisationData(
        favouritesItemIds,
        personalisationResponse,
      )
      recommendationsAPISuccess(personalisationData)
    }).catch((error) => {
      recommendationsAPIFail(error)
    })
  }

  useEffect(() => {
    if (storeId) {
      const params = {
        user_context_info: {
          user_id: profileId,
          store_id: storeId,
        },
        page_info: {
          request_origin: 'asda',
        },
        modules_info: [
          {
            placement: placementId,
            include_sponsored_products: true,
            populate_response: true,
            limit: moduleData.recordsPerPage,
          },
        ],
      }
      getPersonalisationData(params)
    }
  }, [storeId])

  useEffect(() => {
    if (isSignedIn && postCode) {
      // Getting user storeId
      serviceEligibilityAction(postCode)
    }
  }, [isSignedIn, postCode])

  if (!items.length) return null

  const handleFavouriteClick = (item = {}) => {
    const {
      id,
      productDetails: {
        isFavourite,
      } = {},
      taxonomyDetails: {
        deptId,
      },
    } = item
    const params = {
      requestorigin: '',
      token: '',
    }
    if (isFavourite) {
      params.payload = {
        items: [id],
        storeId: '',
      }
      personalisationActionsAPI.removeFavourites(params, profileId)
    } else {
      params.payload = {
        favouriteItems: [{
          skuId: id,
          deptId,
        }],
      }
      personalisationActionsAPI.addFavourites(params, profileId)
    }
  }

  const handleProductClick = ({ productDetails, priceDetails }) => {
    const { placement, strategyId, strategyMessage } = metadata
    // trigger analytics on p13n product click
    trackSignalEvent('P13NProductClick', {
      profileId,
      dateTimeStamp: new Date().toLocaleString(),
      isActive,
      lastLoginAt,
      operatingSystem: getCurrentOS(),
      placement,
      previousPageName: document.referrer || '',
      productString: `${productDetails.id};-${productDetails.name};-${priceDetails.pricePerUnit};-${productDetails.brand}`,
      storeId,
      strategyId,
      strategyMessage,
      url: document.location.href,
    })
  }

  const headerTitle = <Title />

  const commonItemUrlQueryParam = P13N_MODULE_CMPID

  return (
    <div
      className='offers-grid'
      data-module={name}
      data-module-name={name}
      data-module-id={moduleId}
      data-module-type={type}
    >
      <ProductList
        handleProductClick={handleProductClick}
        title={headerTitle}
        items={items}
        handleFavouriteClick={handleFavouriteClick}
        commonItemUrlQueryParam={commonItemUrlQueryParam}
      />
      {/* TODO: remove this condition, when respective page gets developed on groceries */}
      {false && (
        <Button
          // clickHandler={handleClick}
          className='asda-btn--fluid view-offers_btn'
          category='primary'
        >
          View All Your Offers
        </Button>
      )}
    </div>
  )
}

const mapStateToProps = ({ profile, personalizedRecommendation, cmsData: { mainContent } }) => {
  const {
    isSignedIn, profileId, additionalInfo, postCode, storeId,
  } = profile
  const { isActive, lastLoginAt } = additionalInfo || {}
  const P13NModule = mainContent.find(content => content.type === moduleType)
  const placementId = (P13NModule && P13NModule.configs && P13NModule.configs.placementID) || ''
  return {
    isSignedIn,
    profileId,
    isActive,
    lastLoginAt,
    postCode,
    storeId,
    personalizedRecommendation,
    placementId,
  }
}

const mapDispatchToProps = dispatch => ({
  serviceEligibilityAction: bindActionCreators(serviceEligibility, dispatch),
  personalisationActionsAPI: bindActionCreators(personalisationActions, dispatch),
})

P13N.propTypes = {
  isSignedIn: bool,
  profileId: string,
  isActive: string,
  lastLoginAt: number,
  postCode: string,
  storeId: string,
  moduleData: shape({ recordsPerPage: string }),
  personalizedRecommendation: shape({
    items: arrayOf(shape({})),
    metadata: {
      placement: string,
      strategyId: string,
      strategyMessage: string,
    },
  }),
  serviceEligibilityAction: func,
  personalisationActionsAPI: shape({
    laodRecommendations: func,
    removeFavourites: func,
    addFavourites: func,
    fetchFavouritesItemList: func,
    recommendationsAPISuccess: func,
    recommendationsAPIFail: func,
  }),
  name: string,
  moduleId: string,
  type: string,
  placementId: string.isRequired,
}

P13N.defaultProps = {
  isSignedIn: false,
  profileId: '',
  isActive: '',
  lastLoginAt: null,
  postCode: '',
  storeId: '',
  moduleData: {},
  personalizedRecommendation: {},
  serviceEligibilityAction() {},
  personalisationActionsAPI: {
    laodRecommendations() {},
    removeFavourites() {},
    addFavourites() {},
    fetchFavouritesItemList() {},
    recommendationsAPISuccess() {},
    recommendationsAPIFail() {},
  },
  name: null,
  moduleId: null,
  type: null,
}

export default connect(mapStateToProps, mapDispatchToProps)(P13N)



// WEBPACK FOOTER //
// ./components/personalizedRecommendation/index.js